// components/Icon.js
//
// The icons the site has been asking for and not getting.
//
// Fifty-odd places across the create flow, the guest lists and the profiles
// render `<i className="icon-arrow-back" />` — the markup of an icon font that
// no stylesheet on this site defines and no font file ships. Every one of them
// drew a zero-width nothing: a back button with no glyph, a "Next" button that
// was just a word, cover-image actions that looked like empty space.
//
// The fix is not to add the font. The site already depends on react-icons, and
// the app it mirrors uses Ionicons; this maps the Ionicon names the markup
// already speaks onto the react-icons set, so the names in the JSX keep their
// meaning and start drawing something.
//
// Unknown name -> nothing rendered, and a console warning in development. A
// missing glyph should be findable, not silent, but it must never throw in a
// user's face.

import React from 'react';
import {
  IoAdd,
  IoAddCircle,
  IoAddCircleOutline,
  IoAlertCircle,
  IoArrowBack,
  IoArrowForward,
  IoCamera,
  IoCheckmark,
  IoCheckmarkCircle,
  IoChevronDown,
  IoChevronForward,
  IoClose,
  IoCloseCircle,
  IoCloudUpload,
  IoCreate,
  IoCrop,
  IoBedOutline,
  IoCallOutline,
  IoCarOutline,
  IoLinkOutline,
  IoRestaurantOutline,
  IoShirtOutline,
  IoTextOutline,
  IoInformationCircleOutline,
  IoCreateOutline,
  IoColorPalette,
  IoDocumentOutline,
  IoEllipsisHorizontal,
  IoFlash,
  IoGlobeOutline,
  IoImageOutline,
  IoLocation,
  IoLockClosed,
  IoLockClosedOutline,
  IoLocationOutline,
  IoPeople,
  IoPeopleOutline,
  IoPlayCircle,
  IoPricetag,
  IoPricetagOutline,
  IoRemove,
  IoSearch,
  IoSparkles,
  IoTicket,
  IoTrash,
} from 'react-icons/io5';

/** Ionicon name -> component. Keys are exactly what the JSX already says. */
const ICONS = {
  add: IoAdd,
  'add-circle': IoAddCircle,
  'add-circle-outline': IoAddCircleOutline,
  'alert-circle': IoAlertCircle,
  'arrow-back': IoArrowBack,
  'arrow-forward': IoArrowForward,
  camera: IoCamera,
  checkmark: IoCheckmark,
  'checkmark-circle': IoCheckmarkCircle,
  'chevron-down': IoChevronDown,
  'chevron-forward': IoChevronForward,
  // The markup says both; they mean the same arrow.
  'chevron-right': IoChevronForward,
  close: IoClose,
  'close-circle': IoCloseCircle,
  'cloud-upload': IoCloudUpload,
  create: IoCreate,
  crop: IoCrop,
  'bed-outline': IoBedOutline,
  'call-outline': IoCallOutline,
  'car-outline': IoCarOutline,
  'link-outline': IoLinkOutline,
  'restaurant-outline': IoRestaurantOutline,
  'shirt-outline': IoShirtOutline,
  'text-outline': IoTextOutline,
  'information-circle-outline': IoInformationCircleOutline,
  'create-outline': IoCreateOutline,
  'color-palette': IoColorPalette,
  'document-outline': IoDocumentOutline,
  'ellipsis-horizontal': IoEllipsisHorizontal,
  flash: IoFlash,
  'globe-outline': IoGlobeOutline,
  'image-outline': IoImageOutline,
  location: IoLocation,
  'lock-closed': IoLockClosed,
  'lock-closed-outline': IoLockClosedOutline,
  'location-outline': IoLocationOutline,
  people: IoPeople,
  'people-outline': IoPeopleOutline,
  'play-circle': IoPlayCircle,
  pricetag: IoPricetag,
  'pricetag-outline': IoPricetagOutline,
  remove: IoRemove,
  search: IoSearch,
  sparkles: IoSparkles,
  ticket: IoTicket,
  trash: IoTrash,
};

export default function Icon({ name, size = 18, className, style, ...rest }) {
  const Glyph = ICONS[name];

  if (!Glyph) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`<Icon name="${name}"> is not in the icon map (src/components/Icon.js).`);
    }
    return null;
  }

  return (
    <Glyph
      size={size}
      className={className}
      style={style}
      // Icons here always sit beside a label or inside a button that carries
      // its own aria-label, so announcing them would only repeat the label.
      aria-hidden="true"
      focusable="false"
      {...rest}
    />
  );
}
